import type { FC } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useUploadFileProgress } from '../../hooks/use-upload-file-progress';

import { UploadFinished } from './upload-finished';
import { UploadInProgress } from './upload-in-progress';
import { UploadReady } from './upload-ready';

type UploadContainerProps = {
  previewImage?: File | string;
  createVideoMutation: (
    fileName: string
  ) => Promise<{ mediaId: string; uploadUrl: string; videoId: string }>;
  onValidFileSelected?: () => void;
  onUploadVideoComplete?: (props: {
    file: File;
    mediaId: string;
    videoId: string;
  }) => void;
  onUploadInProgress?: () => void;
  onError?: (error: string) => void;
};
export const UploadContainer: FC<UploadContainerProps> = ({
  previewImage,
  createVideoMutation,
  onValidFileSelected,
  onUploadVideoComplete,
  onUploadInProgress,
  onError,
}) => {
  const { $t } = useIntl();

  const [videoId, setVideoId] = useState<string | undefined>();
  const [progress, setProgress] = useState(0);

  const uploadFile = useUploadFileProgress({
    onProgressChange: (newProgress) => {
      setProgress(newProgress);
      onUploadInProgress?.();
    },
  });

  const handleFileSelect = async (file: File): Promise<void> => {
    if (file.size > 5000000000) {
      onError?.($t({ id: 'VIDEO_UPLOADING_ERROR' }));
      return;
    }

    if (file.type.indexOf('video/') < 0) {
      onError?.($t({ id: 'VIDEO_UPLOADING_ERROR' }));
      return;
    }

    onValidFileSelected?.();

    const { mediaId, uploadUrl, videoId } = await createVideoMutation(
      file.name
    );

    setVideoId(videoId);

    try {
      await uploadFile(uploadUrl, file);

      onUploadVideoComplete?.({
        file,
        mediaId,
        videoId,
      });
    } catch {
      onError?.($t({ id: 'VIDEO_UPLOADING_ERROR' }));
    }
  };

  return (
    <>
      {progress === 0 ? (
        <UploadReady onError={onError} handleFileSelect={handleFileSelect} />
      ) : null}
      {progress > 0 && progress < 1 ? (
        <UploadInProgress progress={progress} />
      ) : null}
      {progress === 1 && videoId ? (
        <UploadFinished
          videoId={videoId}
          previewImage={previewImage}
          onError={() => onError?.($t({ id: 'VIDEO_UPLOAD_ERROR' }))}
        />
      ) : null}
    </>
  );
};
